// Word lists and English morphology used by `grammarChecker.ts`.
//
// A direct port of the Android build's `tutor/EnglishWords.kt`. Everything here
// is offline data — no dictionary download, no network, no API key. The verb
// lists are deliberately curated rather than exhaustive: the grammar rules only
// fire on words we recognise, so an unknown word is left untouched instead of
// being mangled by a guess.

/** Base ("dictionary") forms of the verbs the rules are allowed to reshape. */
export const commonVerbs: ReadonlySet<string> = new Set([
  'be', 'have', 'do', 'say', 'go', 'get', 'make', 'know', 'think', 'take',
  'see', 'come', 'want', 'look', 'use', 'find', 'give', 'tell', 'work', 'call',
  'try', 'ask', 'need', 'feel', 'become', 'leave', 'put', 'mean', 'keep', 'let',
  'begin', 'seem', 'help', 'talk', 'turn', 'start', 'show', 'hear', 'play', 'run',
  'move', 'like', 'live', 'believe', 'hold', 'bring', 'happen', 'write', 'provide',
  'sit', 'stand', 'lose', 'pay', 'meet', 'include', 'continue', 'set', 'learn',
  'change', 'lead', 'understand', 'watch', 'follow', 'stop', 'create', 'speak',
  'read', 'spend', 'grow', 'open', 'walk', 'win', 'teach', 'offer', 'remember',
  'love', 'consider', 'appear', 'buy', 'wait', 'serve', 'send', 'build', 'stay',
  'fall', 'cut', 'reach', 'remain', 'study', 'eat', 'drink', 'sleep', 'cook',
  'drive', 'travel', 'listen', 'enjoy', 'finish', 'practice', 'visit', 'wake',
  'arrive', 'clean', 'dance', 'sing', 'swim', 'exercise', 'relax', 'worry',
  'hope', 'plan', 'decide', 'prefer', 'hate', 'miss', 'rest', 'shop', 'cry',
  'laugh', 'smile', 'answer', 'explain', 'describe', 'choose', 'close', 'count',
  'draw', 'drop', 'fix', 'forget', 'join', 'jump', 'kick', 'knock', 'marry',
  'order', 'paint', 'pass', 'pick', 'push', 'pull', 'repeat', 'return', 'ride',
  'ring', 'save', 'share', 'shout', 'sell', 'sound', 'throw', 'touch',
  'wash', 'wear', 'wish', 'check', 'climb', 'collect', 'compare',
  'complain', 'cross', 'discuss', 'download', 'expect', 'fill', 'hurry',
  'improve', 'invite', 'manage', 'mention', 'notice', 'prepare', 'promise',
  'protect', 'realize', 'receive', 'recommend', 'reduce', 'relate', 'reply',
  'report', 'search', 'solve', 'suggest', 'support', 'survive', 'train',
  'translate', 'wonder',
]);

/**
 * Verbs where "am/is/are + BASE" is almost certainly a missing "-ing"
 * ("I am go home" → "I am going home"). Stative verbs such as *like* or *know*
 * are excluded — for those the fix is to drop the *be*, not add *-ing*.
 */
export const progressiveSafeVerbs: ReadonlySet<string> = new Set([
  'go', 'come', 'eat', 'drink', 'work', 'study', 'play', 'run', 'walk', 'watch',
  'read', 'write', 'sleep', 'cook', 'drive', 'travel', 'talk', 'speak', 'learn',
  'listen', 'wait', 'live', 'take', 'make', 'do', 'get', 'sit', 'stand', 'try',
  'help', 'look', 'use', 'meet', 'visit', 'dance', 'sing', 'swim', 'shop',
  'clean', 'practice', 'plan', 'move', 'leave', 'arrive', 'finish', 'start',
  'exercise', 'relax', 'wash', 'prepare', 'search', 'train',
]);

/** Stative verbs: "I am agree" → "I agree" rather than "I am agreeing". */
export const stativeVerbs: readonly string[] = [
  'agree', 'know', 'like', 'love', 'hate', 'want', 'need', 'prefer', 'believe',
  'understand', 'remember', 'forget', 'mean', 'own', 'belong', 'seem',
];

/** base → simple past, for the verbs whose past is irregular. */
export const irregularPast: Readonly<Record<string, string>> = {
  be: 'was', become: 'became', begin: 'began', bring: 'brought',
  build: 'built', buy: 'bought', catch: 'caught', choose: 'chose',
  come: 'came', cost: 'cost', cut: 'cut', do: 'did',
  draw: 'drew', drink: 'drank', drive: 'drove', eat: 'ate',
  fall: 'fell', feel: 'felt', find: 'found', forget: 'forgot',
  get: 'got', give: 'gave', go: 'went', grow: 'grew',
  have: 'had', hear: 'heard', hold: 'held', keep: 'kept',
  know: 'knew', lead: 'led', leave: 'left', lose: 'lost',
  make: 'made', mean: 'meant', meet: 'met', pay: 'paid',
  put: 'put', read: 'read', ride: 'rode', ring: 'rang',
  run: 'ran', say: 'said', see: 'saw', sell: 'sold',
  send: 'sent', set: 'set', sing: 'sang', sit: 'sat',
  sleep: 'slept', speak: 'spoke', spend: 'spent', stand: 'stood',
  swim: 'swam', take: 'took', teach: 'taught', tell: 'told',
  think: 'thought', throw: 'threw', understand: 'understood',
  wake: 'woke', wear: 'wore', win: 'won', write: 'wrote',
};

/** simple past → base, e.g. "went" → "go". */
export const pastToBase: Readonly<Record<string, string>> = (() => {
  const out: Record<string, string> = {};
  for (const [base, past] of Object.entries(irregularPast)) out[past] = base;
  // Past forms that collide with another verb's base form or need a second entry.
  out.were = 'be';
  out.was = 'be';
  out.been = 'be';
  out.gone = 'go';
  out.done = 'do';
  out.seen = 'see';
  out.eaten = 'eat';
  out.taken = 'take';
  out.written = 'write';
  out.spoken = 'speak';
  out.driven = 'drive';
  out.known = 'know';
  out.given = 'give';
  out.gotten = 'get';
  return out;
})();

/** Wrong regularised pasts learners produce, mapped to the real irregular form. */
export const overRegularisedPast: Readonly<Record<string, string>> = {
  goed: 'went', buyed: 'bought', teached: 'taught',
  catched: 'caught', eated: 'ate', drinked: 'drank',
  runned: 'ran', comed: 'came', taked: 'took', maked: 'made',
  thinked: 'thought', bringed: 'brought', sleeped: 'slept',
  writed: 'wrote', speaked: 'spoke', singed: 'sang',
  swimmed: 'swam', feeled: 'felt', finded: 'found',
  gived: 'gave', knowed: 'knew', leaved: 'left', losed: 'lost',
  meeted: 'met', payed: 'paid', readed: 'read', sayed: 'said',
  selled: 'sold', sended: 'sent', sitted: 'sat', standed: 'stood',
  telled: 'told', understanded: 'understood', winned: 'won',
  beginned: 'began', choosed: 'chose', falled: 'fell',
  forgetted: 'forgot', getted: 'got', growed: 'grew',
  haved: 'had', heared: 'heard', holded: 'held', keeped: 'kept',
  weared: 'wore', waked: 'woke', throwed: 'threw',
};

/** Nouns that have no plural in English. */
export const uncountablePlurals: Readonly<Record<string, string>> = {
  informations: 'information', advices: 'advice',
  furnitures: 'furniture', homeworks: 'homework',
  equipments: 'equipment', softwares: 'software',
  knowledges: 'knowledge', luggages: 'luggage',
  baggages: 'baggage', feedbacks: 'feedback',
  researches: 'research', progresses: 'progress',
  traffics: 'traffic', weathers: 'weather', musics: 'music',
  moneys: 'money', breads: 'bread', rices: 'rice',
};

/** Jobs after "am/is/are" that need an article: "I am student" → "I am a student". */
export const jobNouns: readonly string[] = [
  'student', 'teacher', 'doctor', 'nurse', 'engineer', 'designer', 'developer',
  'programmer', 'writer', 'singer', 'driver', 'lawyer', 'manager', 'chef',
  'farmer', 'artist', 'dentist', 'waiter', 'waitress', 'soldier', 'scientist',
  'accountant', 'photographer', 'musician', 'actor', 'actress', 'pilot',
  'beginner', 'freshman', 'housewife', 'researcher', 'consultant',
];

/** Words starting with a vowel letter but a consonant sound → still "a". */
const consonantSoundVowelWords: ReadonlySet<string> = new Set([
  'university', 'universe', 'user', 'unique', 'uniform', 'union', 'united',
  'usual', 'useful', 'european', 'one', 'once', 'eucalyptus', 'ukulele',
]);

/** Words starting with a consonant letter but a vowel sound → still "an". */
const vowelSoundConsonantWords: ReadonlySet<string> = new Set([
  'hour', 'honest', 'honour', 'honor', 'heir', 'honestly', 'hourly',
]);

const VOWELS: ReadonlySet<string> = new Set(['a', 'e', 'i', 'o', 'u']);

export function needsAn(word: string): boolean {
  const w = word.toLowerCase();
  if (consonantSoundVowelWords.has(w)) return false;
  if (vowelSoundConsonantWords.has(w)) return true;
  return VOWELS.has(w.charAt(0));
}

/** Multi-syllable verbs stressed on the last syllable, so the consonant doubles. */
const doublingVerbs: ReadonlySet<string> = new Set([
  'begin', 'forget', 'prefer', 'occur', 'refer', 'permit', 'admit', 'commit',
  'control', 'regret', 'submit', 'upset', 'omit', 'transfer',
]);

/** go → goes, study → studies, watch → watches. */
export function thirdPerson(base: string): string {
  const b = base.toLowerCase();
  switch (b) {
    case 'be':
      return 'is';
    case 'have':
      return 'has';
    case 'do':
      return 'does';
    case 'go':
      return 'goes';
    default:
      break;
  }
  if (
    b.endsWith('s') || b.endsWith('sh') || b.endsWith('ch') ||
    b.endsWith('x') || b.endsWith('z') || b.endsWith('o')
  ) {
    return b + 'es';
  }
  if (b.length > 1 && b.endsWith('y') && !VOWELS.has(b.charAt(b.length - 2))) {
    return b.slice(0, -1) + 'ies';
  }
  return b + 's';
}

/** go → going, make → making, run → running, lie → lying. */
export function gerund(base: string): string {
  const b = base.toLowerCase();
  if (b === 'be') return 'being';
  if (b.endsWith('ie')) return b.slice(0, -2) + 'ying';
  if (b.endsWith('ee') || b.endsWith('oe') || b.endsWith('ye')) return b + 'ing';
  if (b.endsWith('e')) return b.slice(0, -1) + 'ing';
  if (shouldDoubleFinalConsonant(b)) return b + b.charAt(b.length - 1) + 'ing';
  return b + 'ing';
}

/** work → worked, study → studied, go → went, stop → stopped. */
export function past(base: string): string {
  const b = base.toLowerCase();
  const irregular = irregularPast[b];
  if (irregular !== undefined) return irregular;
  if (b.endsWith('e')) return b + 'd';
  if (b.length > 1 && b.endsWith('y') && !VOWELS.has(b.charAt(b.length - 2))) {
    return b.slice(0, -1) + 'ied';
  }
  if (shouldDoubleFinalConsonant(b)) return b + b.charAt(b.length - 1) + 'ed';
  return b + 'ed';
}

/** True for single-syllable consonant-vowel-consonant stems like *stop* or *run*. */
function shouldDoubleFinalConsonant(b: string): boolean {
  // Two-letter verbs (go, do, be, see) never double: "going", not "gooing".
  if (b.length < 3) return false;
  if (doublingVerbs.has(b)) return true;
  const last = b.charAt(b.length - 1);
  const mid = b.charAt(b.length - 2);
  const first = b.charAt(b.length - 3);
  if (VOWELS.has(last) || last === 'w' || last === 'x' || last === 'y') return false;
  if (!VOWELS.has(mid) || VOWELS.has(first)) return false;
  // Only single-syllable stems double; "visit" and "open" must not.
  return [...b].filter((c) => VOWELS.has(c)).length === 1;
}

/** goes → go, studies → study; null when `word` isn't a known third-person form. */
export function baseOfThirdPerson(word: string): string | null {
  const w = word.toLowerCase();
  switch (w) {
    case 'is':
      return 'be';
    case 'has':
      return 'have';
    case 'does':
      return 'do';
    case 'goes':
      return 'go';
    default:
      break;
  }
  if (w.endsWith('ies') && w.length > 3) {
    const candidate = w.slice(0, -3) + 'y';
    if (commonVerbs.has(candidate)) return candidate;
  }
  if (w.endsWith('es') && w.length > 2) {
    const candidate = w.slice(0, -2);
    if (commonVerbs.has(candidate)) return candidate;
  }
  if (w.endsWith('s') && w.length > 1) {
    const candidate = w.slice(0, -1);
    if (commonVerbs.has(candidate)) return candidate;
  }
  return null;
}

/** went → go, worked → work, studied → study; null when not recognisable. */
export function baseOfPast(word: string): string | null {
  const w = word.toLowerCase();
  const known = pastToBase[w];
  if (known !== undefined) return known;
  if (w.endsWith('ied') && w.length > 3) {
    const candidate = w.slice(0, -3) + 'y';
    if (commonVerbs.has(candidate)) return candidate;
  }
  if (w.endsWith('ed') && w.length > 2) {
    const stripped = w.slice(0, -2);
    if (commonVerbs.has(stripped)) return stripped;
    if (stripped.length > 1 && stripped.charAt(stripped.length - 1) === stripped.charAt(stripped.length - 2)) {
      const undoubled = stripped.slice(0, -1);
      if (commonVerbs.has(undoubled)) return undoubled;
    }
    const withE = stripped + 'e';
    if (commonVerbs.has(withE)) return withE;
  }
  return null;
}

/** True when `word` is a base-form verb we are confident about. */
export function isBaseVerb(word: string): boolean {
  return commonVerbs.has(word.toLowerCase());
}
